import { createStore } from 'zustand/vanilla';
import { immer } from 'zustand/middleware/immer';

import { StoreInitializer } from '@/utils/store-middlewares/initializeFromRemote';
import { createContextBoundedStore } from '@/providers/helper/createContextBoundedStore';
import { getLocalStorage } from '@/stores/userDataStore';
import { AssetCreationToolName } from '@/types/media';

const LOCAL_STORAGE_DISMISSED_NOTIFICATIONS_KEY = 'dismissedNotifications';

export type UsedTools = AssetCreationToolName | 'studio_video';

const getStoredDismissedNotifications = () => {
  const dismissedNotifications = getLocalStorage().getItem(LOCAL_STORAGE_DISMISSED_NOTIFICATIONS_KEY);
  try {
    return dismissedNotifications ? JSON.parse(dismissedNotifications) : [];
  } catch (e) {
    return [];
  }
};

export type LibraryNotification = {
  id: string;
  source: 'studio_video' | 'media_asset';
  title: string;
  description: string;
  tone: 'success' | 'info' | 'warning' | 'critical';
  toolUsed: UsedTools;
  generatedAt: string;
  createdAt?: Date;
  error?: string;
};

class LibraryNotificationsStoreState {
  notifications: LibraryNotification[] = [];
  dismissedNotifications: string[] = getStoredDismissedNotifications();
}
type LibraryNotificationsStoreActions = {
  setNotifications: (notifications: LibraryNotification[]) => void;
  addNotification: (notification: LibraryNotification) => void;
  dismissNotification: (id: string) => void;
};

type LibraryNotificationsStore = LibraryNotificationsStoreState & LibraryNotificationsStoreActions;

const libraryNotificationsStore: StoreInitializer<LibraryNotificationsStore> = (set, get) => ({
  ...new LibraryNotificationsStoreState(),

  setNotifications: (notifications: LibraryNotification[]) => {
    set((state) => ({ ...state, notifications }));
  },

  addNotification: (notification: LibraryNotification) => {
    console.log('addNotification', notification);
    notification.createdAt = new Date();
    set((state) => {
      const isDismissed = state.dismissedNotifications.includes(notification.id);
      const isAlreadyInNotifications = state.notifications.some((n) => n.id === notification.id);
      if (isDismissed || isAlreadyInNotifications) return state;
      return {
        ...state,
        notifications: [...state.notifications, notification],
      };
    });
  },

  dismissNotification: (id: string) => {
    set((state) => {
      const newDismissedNotifications = [...state.dismissedNotifications, id];
      getLocalStorage().setItem(LOCAL_STORAGE_DISMISSED_NOTIFICATIONS_KEY, JSON.stringify(newDismissedNotifications));
      return {
        ...state,
        dismissedNotifications: newDismissedNotifications,
      };
    });
  },
});

export const { hook: useLibraryNotificationsStore, provider: LibraryNotificationsStoreProvider } =
  createContextBoundedStore({
    createFunction: () => createStore<LibraryNotificationsStore>()(immer(libraryNotificationsStore)),
  });
